import React from 'react';
import { Button, Popconfirm, message } from 'antd';
import { useSelector } from 'react-redux';
import type { RootState } from 'sama-shared';
import { isDarkTheme } from './brasaTheme';
import { useGetMyLocationsQuery } from '@sama-partner/features/tenant/locations/api/locationsApi';
import { useTogglePartnerLocationOpenMutation } from '@sama-partner/features/tenant/locations/api/locationsApi';

interface StoreStatusPillProps {
  isMobile?: boolean;
}

/*
 * Intent:     el operador debe ver de un golpe si la sede vende y poder
 *             abrir/cerrar sin entrar a Sucursales. Acción destructiva →
 *             siempre con confirmación.
 * Hierarchy:  dot vivo + "Abierta/Cerrada" (focal del header junto a la sede).
 * Palette:    lima translúcido (abierta) / rojo translúcido (cerrada); el dot
 *             abierto pulsa (firma Brasa), el cerrado es estático.
 * Depth:      pastilla inset sobre el header, sin glow salvo hover.
 * Typography: Inter 12.5/700, sin tabular (no hay cifras).
 * Spacing:    base 4 · pill 36px h · radius pill · gap 8.
 */

export const StoreStatusPill: React.FC<StoreStatusPillProps> = ({ isMobile = false }) => {
  const activeStoreId = useSelector((state: RootState) => state.auth.activeStoreId);
  const currentTheme = useSelector((state: RootState) => state.auth.theme);
  const isDark = isDarkTheme(currentTheme);

  const { data: locations = [], isLoading } = useGetMyLocationsQuery(undefined, {
    skip: !activeStoreId,
  });
  const [toggleOpen, { isLoading: isToggling }] = useTogglePartnerLocationOpenMutation();

  if (!activeStoreId) return null;

  const active = locations.find((l) => l.id === activeStoreId);
  const unknown = isLoading && !active;
  const isOpen = active?.isOpenForOrders ?? true;

  const handleToggle = async () => {
    try {
      await toggleOpen({ id: activeStoreId, isOpenForOrders: !isOpen }).unwrap();
      message.success(isOpen ? 'Tienda cerrada para pedidos' : 'Tienda abierta para pedidos');
    } catch {
      message.error('No se pudo cambiar el estado de la tienda');
    }
  };

  const accent = unknown ? (isDark ? '#6f6257' : '#b8a796') : isOpen ? '#a3e635' : '#ff4d5e';
  const ink = unknown
    ? isDark
      ? '#a89885'
      : '#8a7663'
    : isOpen
      ? isDark
        ? '#bef264'
        : '#4d7c0f'
      : isDark
        ? '#ffa39e'
        : '#cf1322';

  const pill = (
    <Button
      type="text"
      loading={isToggling}
      disabled={unknown}
      aria-label={
        unknown
          ? 'Cargando estado de la tienda'
          : isOpen
            ? 'Tienda abierta — clic para cerrar'
            : 'Tienda cerrada — clic para abrir'
      }
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        height: 36,
        padding: isMobile ? '0 10px' : '0 14px',
        borderRadius: 999,
        background: unknown
          ? 'transparent'
          : isOpen
            ? isDark
              ? 'rgba(163,230,53,0.10)'
              : 'rgba(77,124,15,0.10)'
            : isDark
              ? 'rgba(255,77,94,0.10)'
              : 'rgba(207,19,34,0.08)',
        border: `1px solid ${unknown ? (isDark ? 'rgba(255,214,170,0.09)' : 'rgba(120,72,20,0.12)') : `${accent}55`}`,
        color: ink,
        fontSize: 12.5,
        fontWeight: 700,
        cursor: unknown ? 'default' : 'pointer',
      }}
    >
      {!isToggling &&
        (unknown || !isOpen ? (
          <span
            style={{ width: 8, height: 8, borderRadius: '50%', background: accent, flexShrink: 0 }}
          />
        ) : (
          <span className="sama-live-dot" />
        ))}
      {unknown
        ? '···'
        : isToggling
          ? isOpen
            ? 'Cerrando…'
            : 'Abriendo…'
          : isOpen
            ? 'Abierta'
            : 'Cerrada'}
    </Button>
  );

  if (unknown || isToggling) return pill;

  return (
    <Popconfirm
      title={isOpen ? '¿Cerrar la tienda?' : '¿Abrir la tienda?'}
      description={
        isOpen
          ? 'Los clientes dejarán de poder pedir en esta sede.'
          : 'La sede volverá a recibir pedidos.'
      }
      okText={isOpen ? 'Cerrar tienda' : 'Abrir tienda'}
      cancelText="Cancelar"
      okButtonProps={isOpen ? { danger: true } : undefined}
      onConfirm={handleToggle}
      placement="bottomRight"
    >
      {pill}
    </Popconfirm>
  );
};

export default StoreStatusPill;
